import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, Wallet, Clock, AlertTriangle, RefreshCw, Check } from 'lucide-react';
import { expensesApi } from '../../api/endpoints/expenses';
import { StatCard } from '../../components/ui/StatCard';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { PlanPaymentModal } from '../../components/expenses/PlanPaymentModal';
import { formatCurrency, formatDate } from '../../lib/formatters';
import { MEMBERSHIP_PLANS } from '../../lib/constants';
import { useStore } from '../../contexts/StoreContext';

const EXPENSE_LABELS: Record<string, string> = {
  domain: 'Domain Registration',
  membership: 'Membership Plan',
  shippingLabel: 'Shipping Label',
  smsCredits: 'SMS Credits',
  customDevelopment: 'Custom Development',
};

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  paylinks: 'Paylinks',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise',
};

function formatPlan(plan?: string) {
  if (!plan) return 'Free';
  return PLAN_LABELS[plan] ?? (plan.charAt(0).toUpperCase() + plan.slice(1));
}

export function ExpensesPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t, i18n } = useTranslation();
  const { currentStore } = useStore();
  const currency = currentStore?.currency ?? 'SAR';

  // Plan the merchant clicked to renew/switch to → opens the payment chooser.
  const [planTarget, setPlanTarget] = useState<string | null>(null);
  const [renewCycle, setRenewCycle] = useState<'monthly' | 'yearly'>('monthly');

  const { data: balanceData, isLoading: balLoading } = useQuery({
    queryKey: ['expenses-balance', storeId],
    queryFn: () => expensesApi.getBalance(storeId!),
    enabled: !!storeId,
  });

  const { data: membershipData } = useQuery({
    queryKey: ['membership-status', storeId],
    queryFn: () => expensesApi.getMembershipStatus(storeId!),
    enabled: !!storeId,
  });

  const { data: historyData, isLoading: histLoading } = useQuery({
    queryKey: ['expenses-history', storeId],
    queryFn: () => expensesApi.getHistory(storeId!),
    enabled: !!storeId,
  });

  if (balLoading || histLoading) return <LoadingSpinner fullPage />;

  const balance = balanceData?.balance ?? 0;
  const membership = membershipData;
  const expenses = historyData?.expenses ?? [];

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">{t('expenses.title', 'Expenses & Billing')}</h1>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard
          title={t('expenses.balance', 'Balance')}
          value={balance}
          currency={currency}
          icon={<Wallet className="h-5 w-5" />}
        />
        <StatCard
          title={t('expenses.plan', 'Current Plan')}
          value={formatPlan(
            membership?.isExpired
              ? (membership?.plan ?? 'free')
              : (membership?.activePlan ?? membership?.plan ?? 'free'),
          )}
          icon={<CreditCard className="h-5 w-5" />}
        />
        {membership?.endDate && (
          <StatCard
            title={t('expenses.daysRemaining', 'Days Remaining')}
            value={membership.daysRemaining}
            icon={<Clock className="h-5 w-5" />}
          />
        )}
        {membership?.isExpired && (
          <StatCard
            title={t('expenses.status', 'Status')}
            value={t('expenses.expired', 'Expired')}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
        )}
      </div>

      {/* Membership card */}
      {membership && (
        <div className="rounded-xl border border-border-default bg-bg-secondary p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold">{t('expenses.membership', 'Membership')}</h2>
              <p className="text-sm text-text-muted mt-1">
                {membership.startDate && `${formatDate(membership.startDate)} - `}
                {membership.endDate ? formatDate(membership.endDate) : t('expenses.noExpiry', 'No expiry')}
              </p>
            </div>
            <Badge variant={membership.isExpired ? 'danger' : membership.expiresSoon ? 'warning' : 'success'}>
              {membership.isExpired ? t('expenses.expired', 'Expired') : membership.expiresSoon ? t('expenses.expiresSoon', 'Expires Soon') : t('coupons.active')}
            </Badge>
          </div>
        </div>
      )}

      {/* Plan management — renew the current plan or switch to another.
          Always available so a merchant never depends on the 3-day
          "expires soon" badge (or an external pricing link) to renew. */}
      <div className="rounded-xl border border-border-default bg-bg-secondary p-6 mb-6">
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-semibold">{t('expenses.managePlan', 'Manage Plan')}</h2>
            <p className="text-sm text-text-muted mt-1">
              {t('expenses.managePlanDesc', 'Renew your current plan or switch anytime.')}
            </p>
          </div>
          <div className="inline-flex items-center gap-1 rounded-lg border border-border-default bg-bg-primary p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setRenewCycle('monthly')}
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${renewCycle === 'monthly' ? 'bg-accent text-white' : 'text-text-muted'}`}
            >
              {t('expenses.monthly', 'Monthly')}
            </button>
            <button
              type="button"
              onClick={() => setRenewCycle('yearly')}
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${renewCycle === 'yearly' ? 'bg-accent text-white' : 'text-text-muted'}`}
            >
              {t('expenses.yearly', 'Yearly')}
              <span className="ms-1 text-[10px] opacity-80">{t('expenses.save2Months', '2 months free')}</span>
            </button>
          </div>
        </div>

        {/* Expiry / renewal nudge — replaces the "vanished badge" problem with
            a persistent, clickable prompt. */}
        {membership?.isExpired && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t('expenses.renewExpiredDesc', 'Your plan expired and your store reverted to the free plan. Renew now to restore your plan benefits.')}</span>
          </div>
        )}
        {!membership?.isExpired && membership?.expiresSoon && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
            <Clock className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t('expenses.renewSoonDesc', 'Your plan is about to expire. Renew now before it reverts to the free plan.')}</span>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(MEMBERSHIP_PLANS).map(([code, cfg]) => {
            const isCurrent = membership?.plan === code;
            const title = (i18n.language === 'ar' ? cfg.titleAr : cfg.titleEn) ?? code;
            return (
              <div
                key={code}
                className={`flex flex-col rounded-xl border p-4 transition-colors ${
                  isCurrent ? 'border-accent bg-accent/5' : 'border-border-default bg-bg-primary'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{title}</span>
                  {isCurrent && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-medium text-accent">
                      <Check className="h-3 w-3" />
                      {t('expenses.currentPlanBadge', 'Your current plan')}
                    </span>
                  )}
                </div>
                <div className="mt-2 mb-4">
                  <span className="text-lg font-bold">{formatCurrency(renewCycle === 'yearly' ? cfg.priceYearly : cfg.price, currency, i18n.language)}</span>
                  <span className="ms-1 text-xs text-text-muted">{renewCycle === 'yearly' ? t('expenses.perYear', '/ year') : t('expenses.perMonth', '/ month')}</span>
                </div>
                <Button
                  variant={isCurrent ? 'primary' : 'secondary'}
                  size="sm"
                  className="mt-auto w-full"
                  onClick={() => setPlanTarget(code)}
                >
                  {isCurrent ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5" />
                      {t('expenses.renewCta', 'Renew')}
                    </>
                  ) : (
                    t('expenses.selectCta', 'Select')
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Expense history */}
      <h2 className="font-semibold mb-4">{t('expenses.history', 'Expense History')}</h2>
      {expenses.length === 0 ? (
        <EmptyState title={t('expenses.noExpenses', 'No expenses yet')} />
      ) : (
        <div className="rounded-xl border border-border-default bg-bg-secondary overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-default text-text-muted">
                <th className="px-4 py-3 text-start font-medium">{t('common.type')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('common.price')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('expenses.billing', 'Billing')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('expenses.payment', 'Payment')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('common.status')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('common.date')}</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((exp) => (
                <tr key={exp._id} className="border-b border-border-default/50 last:border-0 hover:bg-bg-hover transition-colors">
                  <td className="px-4 py-3 font-medium">{EXPENSE_LABELS[exp.expense] ?? exp.expense}</td>
                  <td className="px-4 py-3">{formatCurrency(exp.amount, currency)}</td>
                  <td className="px-4 py-3 text-text-muted">{exp.billingCycle}</td>
                  <td className="px-4 py-3 text-text-muted">{exp.paymentMethod}</td>
                  <td className="px-4 py-3">
                    <Badge variant={exp.status === 'confirmed' ? 'success' : 'warning'}>
                      {exp.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-text-muted">{formatDate(exp.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment chooser — wallet vs card. Same modal used by the store-picker
          plan deep-link, opened here directly with the chosen plan. */}
      {planTarget && storeId && (
        <PlanPaymentModal
          storeId={storeId}
          storeName={currentStore?.name ?? storeId}
          plan={planTarget}
          cycle={renewCycle}
          onClose={() => setPlanTarget(null)}
        />
      )}
    </div>
  );
}
